import {
  DataTexture,
  LinearFilter,
  NoColorSpace,
  RGBAFormat,
  RGFormat,
  SRGBColorSpace,
  Texture,
} from 'three';

import { TileCoord, TileTextureLoader } from '../ortho-tile-asset/ortho-tile-typings';
import { CogRangeReader } from './cog-range-reader';
import { CogSource, Compression } from './cog-source';

/** Key on texture.userData holding the tile's alpha mask texture, if it has one. */
export const COG_MASK_TEXTURE = 'cogMask';

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('Aborted', 'AbortError');
  }
}

/**
 * Builds a standalone JPEG from an abbreviated tile stream by splicing in the shared tables:
 * tables without their EOI, followed by the tile without its SOI.
 */
function assembleJpeg(tables: Uint8Array | undefined, tile: Uint8Array): Uint8Array {
  if (!tables || tables.length < 4) {
    return tile;
  }
  const jpeg = new Uint8Array(tables.length - 2 + tile.length - 2);
  jpeg.set(tables.subarray(0, tables.length - 2), 0);
  jpeg.set(tile.subarray(2), tables.length - 2);
  return jpeg;
}

async function inflate(bytes: Uint8Array, compression: number | undefined): Promise<Uint8Array> {
  if (compression === Compression.None) {
    return bytes;
  }
  if (compression !== Compression.Deflate && compression !== Compression.AdobeDeflate) {
    throw new Error(`Unsupported COG mask compression ${compression}`);
  }
  // TIFF deflate is zlib-wrapped, which is what the 'deflate' format expects
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Expands a 1-bit, MSB-first mask into RG pixels with the mask (0 or 255) in G, which is the
 * channel three's alphaMap samples. RG rather than luminance because WebGL2 texStorage2D needs a
 * sized format (RG8); LUMINANCE fails to allocate and samples as black, i.e. fully transparent.
 * Flipped vertically to match the image bitmap, which is decoded with imageOrientation: 'flipY'.
 * Returns 'opaque' or 'transparent' for uniform masks, so the tile can skip the mask texture,
 * or the whole tile when nothing in it is visible.
 */
function unpackMask(
  bits: Uint8Array,
  width: number,
  height: number,
): Uint8Array | 'opaque' | 'transparent' {
  if (bits.every((b) => b === 0xff)) {
    return 'opaque';
  }
  if (bits.every((b) => b === 0)) {
    return 'transparent';
  }
  const rowBytes = Math.ceil(width / 8);
  const out = new Uint8Array(width * height * 2);
  for (let y = 0; y < height; y++) {
    const src = y * rowBytes;
    const dst = (height - 1 - y) * width;
    for (let x = 0; x < width; x++) {
      if ((bits[src + (x >> 3)] >> (7 - (x & 7))) & 1) {
        out[(dst + x) * 2 + 1] = 255;
      }
    }
  }
  return out;
}

function createTransparentTexture(): Texture {
  const texture = new DataTexture(new Uint8Array(4), 1, 1, RGBAFormat);
  texture.needsUpdate = true;
  return texture;
}

/**
 * Loads COG tiles as textures. Image and mask come from the same range reader, so for
 * GDAL COGs (mask interleaved right after each tile) both arrive in one request, and
 * adjacent tiles requested in the same frame share a request too.
 *
 * The JPEG is decoded off the main thread by createImageBitmap. The mask is attached as a
 * separate RG texture on texture.userData[COG_MASK_TEXTURE] and applied as alphaMap,
 * so we never have to composite RGBA pixels on the CPU.
 */
export class CogTileLoader implements TileTextureLoader {
  private readonly reader: CogRangeReader;

  constructor(private readonly source: CogSource) {
    this.reader = new CogRangeReader(source.url);
  }

  async loadTileTexture(coord: TileCoord, signal?: AbortSignal): Promise<Texture> {
    throwIfAborted(signal);

    const tile = this.source.getTile(coord);
    if (!tile) {
      throw new Error(`COG tile ${coord.z}/${coord.x}/${coord.y} is out of range`);
    }
    // Sparse tile: GDAL omits blocks with no data
    if (!tile.imageByteCount) {
      return createTransparentTexture();
    }

    // Both reads must be issued synchronously so the reader can batch them with this frame's other tiles
    const imageRead = this.reader.read(
      tile.imageOffset,
      tile.imageOffset + tile.imageByteCount,
      signal,
    );
    const maskRead =
      tile.maskOffset !== undefined && tile.maskByteCount
        ? this.reader.read(tile.maskOffset, tile.maskOffset + tile.maskByteCount, signal)
        : undefined;

    const bitmapPromise = imageRead.then((bytes) =>
      createImageBitmap(
        new Blob([assembleJpeg(this.source.jpegTables, bytes)], { type: 'image/jpeg' }),
        // Pre-flip to WebGL convention, same as the XYZ TileLoader
        { imageOrientation: 'flipY' },
      ),
    );
    const maskPromise = maskRead?.then(async (bytes) =>
      unpackMask(
        await inflate(bytes, this.source.maskCompression),
        this.source.tileWidth,
        this.source.tileHeight,
      ),
    );

    let bitmap: ImageBitmap;
    let mask: Uint8Array | 'opaque' | 'transparent' | undefined;
    try {
      [bitmap, mask] = await Promise.all([bitmapPromise, maskPromise]);
    } catch (err) {
      // If only the mask failed, the bitmap may still arrive; release it
      void bitmapPromise.then(
        (b) => b.close(),
        () => undefined,
      );
      throw err;
    }

    if (signal?.aborted) {
      bitmap.close();
      throwIfAborted(signal);
    }
    // Nothing visible (typically outside the orthophoto's footprint): skip the GPU upload
    if (mask === 'transparent') {
      bitmap.close();
      return createTransparentTexture();
    }

    const texture = new Texture(bitmap);
    texture.colorSpace = SRGBColorSpace;
    texture.flipY = false;
    texture.minFilter = LinearFilter;
    // Smooth like the XYZ tiles; OrthoTileAsset sets the filter from the orthophoto settings.
    // Each tile is its own texture, so linear magnification can't blend across tile borders and
    // leaves a step there when zoomed far in. Nearest (pixelated) shows plain pixels instead.
    texture.magFilter = LinearFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;

    if (mask instanceof Uint8Array) {
      const maskTexture = new DataTexture(
        mask,
        this.source.tileWidth,
        this.source.tileHeight,
        RGFormat,
      );
      maskTexture.colorSpace = NoColorSpace;
      maskTexture.minFilter = LinearFilter;
      maskTexture.magFilter = LinearFilter;
      maskTexture.generateMipmaps = false;
      maskTexture.unpackAlignment = 1;
      maskTexture.needsUpdate = true;

      texture.userData[COG_MASK_TEXTURE] = maskTexture;
      texture.addEventListener('dispose', () => maskTexture.dispose());
    }

    return texture;
  }
}
